import React, { useContext, useEffect, useState } from "react";
import { statusDescription } from "shared/constants/task-status";
import { SourcePage } from "shared/taskTypeMap";
import { ProductsContext } from "../../../contexts/attributes";
import { useProductValidation } from "shared/hooks/use-product-validation";
import * as notify from "shared/notify";
import { completeTask, editTask, validateTask } from "shared/taskState";
import { useAttributeGroups } from "shared/hooks/api/use-attribute-groups";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "../../tooltip/tooltip";
import { getProductName } from "shared/product-name";

export const ProductLabel = ({ fullUpc, taskInfo, upc, readOnlyComplete, missingAttributesList, taskType, ...props }) => {
    const { products } = useContext(ProductsContext);
    const validateFields = useProductValidation(products);
    const productName = getProductName(products[upc.codingWipKey]);
    const [isProductInvalid, setProductInvalidStatus] = useState(true);
    const [hasMissingSizeAttributes, showMissingSizeAttributes] = useState(false);
    const [itemInfo] = taskInfo.filter(({ taskContextKey }) => taskContextKey === upc.key);
    const [status] = statusDescription.filter(({ value }) => value === itemInfo?.state);
    const stateText = status?.text;

    const taskId = itemInfo && itemInfo.key;
    const { data: definitions } = useAttributeGroups(["IntakeProfilingNew"], { codingWipKey: upc.codingWipKey });

    useEffect(() => {
        if (!isProductInvalid) {
            finishTask();
        }
    }, [isProductInvalid]);

    useEffect(() => {
        if (missingAttributesList?.includes(upc.codingWipKey)) {
            showMissingSizeAttributes(true);
        } else {
            showMissingSizeAttributes(false);
        }
    }, [missingAttributesList]);

    const handleTaskCompletion = (e) => {
        e.stopPropagation();

        const invalidFields = validateFields(definitions, upc.codingWipKey);

        if (invalidFields.length > 0) {
            setProductInvalidStatus(true);
            notify.error("Required fields are missing.");

            return;
        }

        setProductInvalidStatus(false);
    };

    const finishTask = async () => {
        if (isProductInvalid) {
            return;
        }

        const serverValidate = await validateTask(upc.profilingTaskKey, false, { passthrough: true, displayError: true });

        if (serverValidate.errors && serverValidate.errors.length > 0) {
            setProductInvalidStatus(true);

            return;
        }

        const confirm = await notify.confirm("Are you sure you want to complete this product in the " + stateText + " state?");

        if (!confirm) {
            setProductInvalidStatus(true);

            return;
        }

        const response = await completeTask(taskId, false, false, SourcePage[taskType], "complete");

        if (!response) {
            return;
        }

        notify.success("Saved!");
        props.refresh(true);
    };

    const updateTask = async (e) => {
        e.stopPropagation();

        const confirm = await notify.confirm("Are you sure you want to edit this product?");

        if (!confirm) {
            return;
        }

        const response = await editTask(taskId, false, false, SourcePage[taskType]);

        setProductInvalidStatus(true);

        if (!response) {
            return;
        }

        props.refresh(true);
    };

    const showCompleteTaskButton = itemInfo?.state !== "completed" && !upc.isFinalized;
    const showUpdateTaskButton = itemInfo?.state === "completed" && !upc.isFinalized;

    const handleCopyUpc = async (e) => {
        e.stopPropagation();
        await navigator.clipboard.writeText(fullUpc);
        notify.success("UPC copied to clipboard!");
    };

    return (
        <div className="justify-content-between align-center">
            <div className="flex gap-8 align-center justify-start">
                <h2 className="m-0">{fullUpc}</h2>
                {productName && (
                    <h2
                        className="m-0 txt-normal ellipsis -capped"
                        title={productName}
                    >
                        {productName}
                    </h2>
                )}
                <Tooltip text="Copy/Paste UPC">
                    <button
                        type="button"
                        className="button -small m-l-8"
                        onClick={handleCopyUpc}
                    >
                        <FontAwesomeIcon icon={faCopy} />
                    </button>
                </Tooltip>
            </div>
            <div className="flex align-center">
                {stateText && (
                    <div className="flex text-inner-status">
                        <label className="m-r-4">Status:</label>
                        <span className="txt-normal">{stateText}</span>
                    </div>
                )}
                {showCompleteTaskButton && (
                    <div className="m-l-20">
                        <button
                            className="button -primary"
                            onClick={(e) => handleTaskCompletion(e)}
                            disabled={props.readOnlyComplete || hasMissingSizeAttributes}
                        >
                            Complete
                        </button>
                    </div>
                )}
                {showUpdateTaskButton && (
                    <div className="m-l-20">
                        <button
                            className="button -primary"
                            onClick={(e) => updateTask(e)}
                            disabled={props.readOnlyEdit || props.isCompleted}
                        >
                            Edit
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
